import { buildImgSrc } from '@/helpers/image-url'
import { useIntl } from '@/intl'
import { useBookingDraftQuery, useBookingDraftQueryKey, useGetStepsTabs } from '@/scopes/checkout/domain/hooks'

import { OverviewBusSummary, OverviewGenericSummary } from '@/scopes/checkout/domain/types'
import { Button, Image, SpaceBetween, tk, Typography, verticalGap } from '@sunwebgroup/ui-components'
import { Icon } from '@sunwebgroup/ui-components/uds'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter, useSearchParams } from 'next/navigation'
import { values } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'

const StyledImage = styled(Image.Root)`
  object-fit: cover;
  display: block;
  border-radius: ${tk('radius_large')};
`

const Wrapper = styled(SpaceBetween)`
  background: ${tk('color_semantic_bg')};
  box-shadow: ${tk('shadow_lg_bottom')};
  border-radius: ${tk('radius_large')};
  ${verticalGap('spacing_xs')}
  align-self: start;
  width: 100%;
  padding: ${tk('spacing_lg')};
`

const AccommodationName = styled(Typography)`
  color: ${tk('color_semantic_text')};
`
const Location = styled(Typography)`
  align-items: center;
  color: ${tk('color_semantic_text_subdued')};
  display: flex;
  margin-top: ${tk('spacing_xs')};

  & > *:first-child {
    flex-shrink: 0;
    margin-right: ${tk('spacing_2xs')};
  }
`

const Content = styled(SpaceBetween)``

const BookingDetails = styled(Typography)`
  color: ${tk('color_semantic_text')};

  &:first-letter {
    text-transform: uppercase;
  }
`
const EditButton = styled(Button)`
  width: fit-content;
`

const OverviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk('spacing_md')};

  @media ${tk('mq_desktop_md')} {
    flex-direction: row;
    justify-content: space-between;
  }
`

export function CheckoutSummary() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()

  if (isLoading)
    return ''
  const basketInformation = data?.basketInformation
  if (basketInformation && !basketInformation.accommodationName)
    return ''
  const summariesInformation = data?.summaries
  const busSummary = summariesInformation?.find(s => s.type === 'bus') as OverviewBusSummary | undefined
  const imageUrl = basketInformation?.imageUrl ?? ''
  const noContentFallbackText = intl.formatMessage({ id: 'no_image' })
  const { participants, groupInformation } = data ?? {}

  const startDate = basketInformation?.accommodationDates?.startDate ? new Date(basketInformation.accommodationDates.startDate) : null
  const endDate = basketInformation?.accommodationDates?.endDate ? new Date(basketInformation.accommodationDates.endDate) : null

  return (
    <Wrapper direction="y" spacing={tk('spacing_lg')}>
      <SpaceBetween direction="x" spacing={tk('spacing_md')}>
        <StyledImage
          defaultSource={{ src: buildImgSrc(imageUrl, 80, 80), width: 80, height: 80 }}
          loaderSource={{ src: buildImgSrc(imageUrl, 20) }}
          sources={[
            {
              src: buildImgSrc(imageUrl, 320, 180),
              width: 320,
              height: 180,
              breakpoint: 'breakpoint_desktop_sm',
            },
            {
              src: buildImgSrc(imageUrl, 240, 160),
              width: 240,
              height: 160,
              breakpoint: 'breakpoint_tablet',
            },
          ]}
          alternativeText={basketInformation?.accommodationName ?? ''}
        >
          <Image.Error>
            <Icon glyph="ImageSlashed" size={32} />
            <Typography $variant="body-lg-bold">{noContentFallbackText}</Typography>
          </Image.Error>
        </StyledImage>

        <Content direction="y" spacing={tk('spacing_md')}>
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            <AccommodationName $variant="heading-lg" as="span">
              {basketInformation?.accommodationName}
            </AccommodationName>
            <Location $variant="body-md">{basketInformation?.location}</Location>
          </SpaceBetween>
          <SpaceBetween direction="y" spacing={tk('spacing_2xs')}>
            {basketInformation?.accommodationDates && (
              <BookingDetails $variant="body-md">
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_overview_step_accommodation_summary' },
                  { startDate: startDate?.toLocaleString(intl.locale, { weekday: 'long', day: 'numeric', month: 'short' }), endDate: endDate?.toLocaleString(intl.locale, { weekday: 'long', day: 'numeric', month: 'short', year: 'numeric' }), duration: basketInformation?.accommodationDates?.duration },
                )}
                {!basketInformation?.accommodationDates && (
                  basketInformation?.departureDate
                )}
              </BookingDetails>
            )}

            <BookingDetails $variant="body-md">
              {!basketInformation?.accommodationDates && (
                basketInformation?.duration
              )}
              {' '}
              {participants && values(participants).length > 1 && `, ${basketInformation?.occupancy}`}
            </BookingDetails>
            {busSummary && (
              <BookingDetails $variant="body-md">
                {' '}
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_overview_step_bus_summary' },
                  { outboundDate: busSummary.outboundDate, inboundDate: busSummary.inboundDate },
                )}
              </BookingDetails>
            )}
            {groupInformation && (
              <BookingDetails $variant="body-md">
                {' '}
                {intl.formatMessage(
                  { id: 'groups_checkout_steps_basket_group_information' },
                  { groupName: groupInformation.name, groupSize: groupInformation.numberOfParticipants },
                )}
              </BookingDetails>
            )}
          </SpaceBetween>
          <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
            <BookingDetails $variant="body-md-bold">
              {intl.formatMessage(
                { id: 'groups_checkout_steps_overview_step_total_price' },
                { totalPrice: basketInformation?.totalPrice },
              )}
            </BookingDetails>
          </SpaceBetween>
        </Content>
      </SpaceBetween>
      <OverviewContent>
        <ParticipantDetails />
        <RoomAndTransportDetails />
        <ServicesDetails />
      </OverviewContent>
    </Wrapper>
  )
}

function ParticipantDetails() {
  const intl = useIntl()
  const { isLoading, isFetching, data } = useBookingDraftQuery()
  const { items: stepItems, activeStep } = useGetStepsTabs()
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const queryKey = useBookingDraftQueryKey()
  React.useEffect(() => {
    queryClient.invalidateQueries({ queryKey })
  }, [])

  if (isLoading || isFetching)
    return ''
  const personalDetailsStep = stepItems[stepItems.findIndex(step => step.text === activeStep) - 1]
  const participantsData = data?.participants

  const handleEditClick = () => {
    if (!personalDetailsStep)
      return ''
    router.push(`${personalDetailsStep.url}?sid=${searchParams.get('sid')}`)
  }

  if (!participantsData)
    return ''

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      <Typography $variant="body-lg-bold">
        {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_participants_summary_title' })}
      </Typography>
      <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
        {Object.values(participantsData).map((participant) => {
          if (!participant.name)
            return ''
          return (
            <SpaceBetween direction="y" spacing={tk('spacing_2xs')} key={participant.id}>
              {participant.name && (
                <Typography $variant="body-md">
                  {intl.formatMessage({ id: `groups_personal_data_step_field_title_${participant.title}_text` })}
                  {' '}
                  {participant.name}
                  {' '}
                  {participant.surname}
                </Typography>
              )}
              {participant.birthdate && <Typography $variant="body-md">{formatDate(new Date(participant.birthdate))}</Typography>}
              {participant.mobilePhoneNumber && <PhoneNumber phone={participant.mobilePhoneNumber} />}
              {participant.emergencyPhoneNumber && <PhoneNumber phone={participant.emergencyPhoneNumber} />}
              {participant.email && <Typography $variant="body-md">{participant.email}</Typography>}
              <EditButton $hierarchy="secondary" $withoutColor $size="small" onClick={handleEditClick}>
                {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_participants_summary_edit_button' })}
              </EditButton>
            </SpaceBetween>
          )
        })}
      </SpaceBetween>
    </SpaceBetween>
  )
}

function formatDate(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0') // leading zero
  const year = date.getFullYear()

  return `${day}-${month}-${year}`
}

function PhoneNumber({ phone }: { phone: { countryCode: string, phoneNumber: string } }) {
  const { countryCode, phoneNumber } = phone
  return (
    <Typography $variant="body-md">
      +
      {countryCode}
      {phoneNumber}
    </Typography>
  )
}
function RoomAndTransportDetails() {
  const { isLoading } = useBookingDraftQuery()
  if (isLoading)
    return ''

  return <></>
}

function ServicesDetails() {
  const intl = useIntl()
  const { isLoading, data } = useBookingDraftQuery()
  const { items: stepItems, activeStep } = useGetStepsTabs()
  const router = useRouter()
  const searchParams = useSearchParams()

  if (isLoading)
    return ''
  const extrasStep = stepItems[stepItems.findIndex(step => step.text === activeStep) - 2]

  const handleEditClick = () => {
    if (!extrasStep)
      return ''
    router.push(`${extrasStep.url}?sid=${searchParams.get('sid')}`)
  }

  return (
    <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
      <Typography $variant="body-lg-bold">
        {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_services_summary_title' })}
      </Typography>
      <SpaceBetween direction="y" spacing={tk('spacing_2xs')}>
        {data?.summaries
          ?.filter(s => s.type === 'generic')
          .map((service) => {
            const busService = service as OverviewGenericSummary // TypeScript cast
            return busService.lines.map((line, index) => (
              <Typography key={index} $variant="body-md">
                {line}
              </Typography>
            ))
          })}
        <EditButton $hierarchy="secondary" $withoutColor $size="small" onClick={handleEditClick}>
          {intl.formatMessage({ id: 'groups_checkout_steps_overview_step_participants_summary_edit_button' })}
        </EditButton>
      </SpaceBetween>
    </SpaceBetween>
  )
}
